// @ts-nocheck
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface SearchedAthlete {
  id: string;
  first_name: string | null;
  last_name: string | null;
  username: string | null;
  avatar_url: string | null;
  primary_sport_id: string | null;
  isFollowing: boolean;
}

export function useAthleteSearch(searchQuery: string) {
  const { user } = useAuth();
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  return useQuery({
    queryKey: ['athlete-search', debouncedQuery, user?.id],
    queryFn: async () => {
      if (!debouncedQuery || debouncedQuery.length < 2) {
        return [];
      }

      // Search profiles by name or username
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, username, avatar_url, primary_sport_id')
        .eq('role', 'athlete')
        .or(`first_name.ilike.%${debouncedQuery}%,last_name.ilike.%${debouncedQuery}%,username.ilike.%${debouncedQuery}%`)
        .limit(20);

      if (error) throw error;
      if (!profiles) return [];

      // Get follow status for each profile
      let followingIds: string[] = [];
      if (user?.id) {
        const { data: follows } = await supabase
          .from('profile_follows')
          .select('followed_id')
          .eq('follower_id', user.id)
          .in('followed_id', profiles.map(p => p.id));

        followingIds = follows?.map(f => f.followed_id) || [];
      }

      return profiles.map((profile) => ({
        ...profile,
        isFollowing: followingIds.includes(profile.id),
      })) as SearchedAthlete[];
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: 1000 * 60 * 2,
  });
}
